import * as uuid from 'uuid';
import { DEFAULT_CURRENCY_CODE, Inject, Injectable } from '@angular/core';
import SPaymentPOS, {
  ReconciliationResult,
  TransactionResult,
  UnexpectedErrorMessage
} from '../../plugins/s-payment-pos';
import { PaymentMethodType } from './models/payment.models';
import { DataService } from './data.service';
import { Settings } from './models/models';
import { APP_SETTINGS } from './settings-data.provider';

@Injectable({ providedIn: 'root' })
export class PaymentService {
  constructor(private readonly dataService: DataService,
    @Inject(APP_SETTINGS) private readonly settings: Settings,
    @Inject(DEFAULT_CURRENCY_CODE) private readonly currency: string,
  ) {}

  async callSPaymentPOS(): Promise<TransactionResult> {
    const paymentMethod = this.settings.paymentMethods.find(({ gateway }) => gateway === 'versa_commerce_pos_spos');

    if (!paymentMethod) {
      throw new Error('No external payment defined in current settings');
    }

    const hasConfiguredSPayPOS = await this.isSPaymentPOSAvailable();
    if (!hasConfiguredSPayPOS) {
      throw new Error('S-Payment POS app is not available!');
    }

    try {
      const transactionId = uuid.v4();
      const transactionResult = await this.executePayment(transactionId, this.dataService.cart.totalPrice, this.currency);
      if (transactionResult.isFailed) {
        return transactionResult;
      }
      await this.dataService.updateCartAttributes({
        attributes: {
          spos_payment_transaction_id: transactionId,
          spos_payment_transaction_data: transactionResult.transactionData,
          spos_payment_customer_receipt: transactionResult.customerReceipt,
          spos_payment_merchant_receipt: transactionResult.merchantReceipt,
          spos_payment_card_circuit: transactionResult.cardCircuit,
        }
      });
      await this.dataService.checkoutCart({
        method: PaymentMethodType.creditCard,
        cardType: 'cc', // FIXME: ???
        paymentMethod,
        isSPOS: true,
      });
      return transactionResult;
    } catch (error: any) {
      if (isSPaymentPOSTechnicalError(error)) {
        throw new SPaymentPOSTechnicalError(error.message, error.data);
      }
      throw error;
    }
  }

  private async executePayment(transactionId: string, amount: number, currency: string): Promise<TransactionResult> {
    const { transactionResult } = await SPaymentPOS.startTransaction({
      amount: this.getChargedAmount(amount),
      currency: currency,
      transactionId: transactionId
    });
    return transactionResult;
  }

  async executePaymentRefund(transactionId: string, amount: number, currency: string): Promise<TransactionResult> {
    const { transactionResult } = await SPaymentPOS.startRefundTransaction({
      amount: this.getChargedAmount(amount),
      currency: currency,
      transactionId: transactionId
    });
    return transactionResult;
  }

  async executePaymentReversal(transactionId: string, amount: number, currency: string, paymentTransactionData: string): Promise<TransactionResult> {
    const { transactionResult } = await SPaymentPOS.startReversalTransaction({
      amount: this.getChargedAmount(amount),
      currency: currency,
      transactionId: transactionId,
      transactionData: paymentTransactionData
    });
    return transactionResult;
  }

  async isSPaymentPOSAvailable(): Promise<boolean> {
    try {
      const { isAvailable } = await SPaymentPOS.isAvailable();
      return isAvailable;
    }
    catch (error: any) {
      // on the desktop version plugins are not supported and Capacitor throws an exception
      if (error.code === 'UNIMPLEMENTED') {
        return false;
      }
      throw error;
    }
  }

  private getChargedAmount(amount: number): string {
    let absoluteAmount = Math.abs(amount);
    return absoluteAmount.toString();
  }

  async createZReport(): Promise<ReconciliationResult> {
    try {
      const { reconciliationResult } = await SPaymentPOS.startReconciliation();
      return reconciliationResult;
    } catch (error: any) {
      if (isSPaymentPOSTechnicalError(error)) {
        throw new SPaymentPOSTechnicalError(error.message, error.data);
      }
      throw error;
    }
  }

}

export class SPaymentPOSTechnicalError extends Error {
  data?: UnexpectedErrorMessage;

  constructor(message: string, data?: UnexpectedErrorMessage) {
    super();
    this.message = message;
    this.data = data;
    this.name = 'SPaymentPOSTechnicalError';
  }

}

export function isSPaymentPOSUnexpectedError(error: any) {
  return error.message === 'S_POS_UNEXPECTED';
}

export function isSPaymentPOSSwitchModeNotConnectedError(error: any) {
  return error.message === 'S_SWITCH_NOT_CONNECTED';
}

export function isSPaymentPOSTechnicalError(error: any) {
  return isSPaymentPOSSwitchModeNotConnectedError(error)
    || isSPaymentPOSUnexpectedError(error)
    || error instanceof SPaymentPOSTechnicalError
    || error?.rejection instanceof SPaymentPOSTechnicalError;
}
